import React, { useState, useEffect } from "react";
import "../../css/CRUDModal.css";
import AddCompanyImage from "../../assets/Icons/Add.png";

const RcsAgentDetailsModal = ({
  show,
  handleClose,
  rcsAgentData,
  onSubmit,
  readOnly = false,
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Set default status to 'A' (Active) if not provided
    setFormData({
      ...rcsAgentData,
      status: rcsAgentData?.status || "A",
    });
    setErrors({}); // Clear errors when new data is loaded
  }, [rcsAgentData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const validInputRegex = /^[a-zA-Z0-9-]*$/;

    if (name === "status") {
      // Status is now handled via dropdown, no additional validation needed
      // since options are controlled
      setErrors({ ...errors, [name]: "" });
      setFormData({ ...formData, [name]: value });
    } else if (
      name === "agentId" ||
      name === "agentApiKey" ||
      name === "apiKey"
    ) {
      // Validation for agentId and agentApiKey
      if (!validInputRegex.test(value)) {
        setErrors({
          ...errors,
          [name]: "Only alphanumeric characters and hyphens are allowed",
        });
      } else {
        setErrors({ ...errors, [name]: "" });
      }
      const filteredValue = value.replace(/[^a-zA-Z0-9-]/g, "");
      setFormData({ ...formData, [name]: filteredValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = () => {
    // Only submit if no errors
    if (Object.values(errors).every((error) => !error)) {
      onSubmit(formData);
    }
  };

  const tableData = [
    { label: "RCS Agent Id", key: "rcsAgentId", readOnly: true },
    { label: "Company Name", key: "companyName", readOnly: true },
    { label: "Agent Id", key: "agentId", readOnly },
    // Show the old agentApiKey only when the backend still returns one
    ...(rcsAgentData?.agentApiKey
      ? [{ label: "Legacy Agent API Key", key: "agentApiKey", readOnly }]
      : []),
    { label: "Agent API Key", key: "apiKey", readOnly },
    { label: "Status", key: "status", readOnly },
  ];

  // Map backend status values to display values
  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Deactive", label: "Deactive" },
  ];

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "550px",
                borderRadius: "16px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  padding: "1rem 0rem",
                  borderBottom: "1px solid #dee2e6",
                }}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={AddCompanyImage}
                    alt="add-company-img"
                    className="h-11 w-11"
                  />
                  <h5 className="modal-title main-title font-medium">
                    RCS Agent Details
                  </h5>
                </div>
              </div>
              <form>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <table
                    className="custom-table"
                    style={{
                      width: "100%",
                      tableLayout: "fixed",
                      borderCollapse: "collapse",
                    }}
                  >
                    <tbody>
                      {tableData.map((row, idx) => (
                        <tr
                          key={idx}
                          style={{ borderBottom: "1px solid #dee2e6" }}
                        >
                          <th
                            style={{
                              textAlign: "left",
                              padding: "8px",
                              backgroundColor: "#f8f9fa",
                              width: "32%",
                              fontWeight: 600,
                            }}
                          >
                            {row.label}
                          </th>
                          <td style={{ padding: "8px" }}>
                            {row.key === "status" ? (
                              <select
                                name={row.key}
                                value={formData[row.key] || "A"}
                                onChange={handleChange}
                                disabled={row.readOnly}
                                style={{
                                  width: "100%",
                                  padding: "4px",
                                  border: errors[row.key]
                                    ? "1px solid #dc3545"
                                    : "1px solid #dee2e6",
                                  backgroundColor: row.readOnly
                                    ? "#f8f9fa"
                                    : "white",
                                }}
                                aria-describedby={`${row.key}-error`}
                              >
                                {statusOptions.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                name={row.key}
                                value={formData[row.key] || ""}
                                onChange={handleChange}
                                disabled={row.readOnly}
                                style={{
                                  width: "100%",
                                  padding: "4px",
                                  border: row.readOnly
                                    ? "none"
                                    : errors[row.key]
                                    ? "1px solid #dc3545"
                                    : "1px solid #dee2e6",
                                  backgroundColor: row.readOnly
                                    ? "#f8f9fa"
                                    : "white",
                                }}
                                aria-describedby={`${row.key}-error`}
                              />
                            )}
                            {errors[row.key] && (
                              <div
                                id={`${row.key}-error`}
                                style={{
                                  color: "#dc3545",
                                  fontSize: "0.8rem",
                                  marginTop: "4px",
                                }}
                              >
                                {errors[row.key]}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-back !rounded-lg"
                    onClick={handleClose}
                  >
                    {readOnly ? "Close" : "Cancel"}
                  </button>
                  {!readOnly && (
                    <button
                      type="button"
                      className="btn btn-submit !rounded-lg"
                      onClick={handleSubmit}
                      disabled={Object.values(errors).some((error) => error)}
                    >
                      Update
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RcsAgentDetailsModal;
